import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Loader2, Trash2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../AuthContext";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Employee removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="team-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="overline mb-3">Access & Roles</div>
          <h1 className="font-display text-4xl md:text-5xl leading-none text-neutral-50">
            The <span className="italic text-[#D4AF37]">Team</span>
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">
            {users.length} account{users.length !== 1 && "s"} · admin adds employees
          </p>
        </div>
        <button
          data-testid="new-employee-btn"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#08080A] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B]"
        >
          <Plus className="w-4 h-4" /> Add Employee
        </button>
      </header>

      {loading ? (
        <div className="text-neutral-400 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="border border-white/8 bg-[#0C0C10] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[0.65rem] tracking-[0.24em] uppercase text-neutral-500 border-b border-white/8">
                <th className="text-left px-6 py-4">Name</th>
                <th className="text-left px-6 py-4">Email</th>
                <th className="text-left px-6 py-4">Designation</th>
                <th className="text-left px-6 py-4">Role</th>
                <th className="px-6 py-4 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`team-row-${u.id}`}
                  className="border-b border-white/5 hover:bg-white/[0.02]"
                >
                  <td className="px-6 py-4 text-neutral-100">
                    <div className="flex items-center gap-2">
                      {u.role === "admin" && (
                        <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
                      )}
                      {u.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-neutral-400">{u.email}</td>
                  <td className="px-6 py-4 text-neutral-400">{u.designation || "—"}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-0.5 text-[0.6rem] tracking-widest uppercase border ${
                        u.role === "admin"
                          ? "border-[#D4AF37] text-[#D4AF37]"
                          : "border-white/15 text-neutral-400"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {u.id !== user.id && u.role !== "admin" && (
                      <button
                        data-testid={`remove-${u.id}`}
                        onClick={() => remove(u)}
                        className="text-neutral-500 hover:text-red-400"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <NewEmployeeDialog
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function NewEmployeeDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    designation: "",
    phone: "",
    role: "employee",
  });
  const [saving, setSaving] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.name} added.`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto p-4"
      onClick={onClose}
      data-testid="new-employee-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-[#0C0C10] border border-white/10 my-8"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/8">
          <div>
            <div className="overline">Add Employee</div>
            <div className="font-display text-2xl text-neutral-50 mt-1">
              Grant portal access
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-neutral-100">
            <X />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Full Name *"><input required value={form.name} onChange={change("name")} className={inp} /></Field>
          <Field label="Email *"><input required type="email" value={form.email} onChange={change("email")} className={inp} placeholder="employee@rudraempire.com" /></Field>
          <Field label="Temporary Password *">
            <input required type="text" minLength={6} value={form.password} onChange={change("password")} className={inp} placeholder="min 6 characters" />
            <div className="text-[0.65rem] text-neutral-500 mt-1">Share this password with the employee securely — they can use it to sign in.</div>
          </Field>
          <Field label="Designation"><input value={form.designation} onChange={change("designation")} className={inp} placeholder="Land Acquisition Manager" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={change("phone")} className={inp} /></Field>
          <Field label="Role">
            <select value={form.role} onChange={change("role")} className={inp}>
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
        </div>

        <div className="p-6 border-t border-white/8 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="px-6 py-3 text-xs tracking-[0.24em] uppercase text-neutral-400 hover:text-neutral-100">Cancel</button>
          <button type="submit" data-testid="employee-submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#08080A] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B] disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Add Employee
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full bg-[#08080A] border border-white/10 py-2.5 px-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D4AF37]";
function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
    </div>
  );
}
